import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  VStack,
  HStack,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Icon,
  useToast,
  Spinner,
  Center
} from '@chakra-ui/react';
import { BsFileText, BsClockHistory, BsCheckCircle, BsCloudUpload } from 'react-icons/bs';
import axios from 'axios';
import Header from '../../../components/Header';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../../utils/DefaultFunction';

const API_URL = `${process.env.REACT_APP_API_URL}/customer-support`;



export default function CreateTicket() {
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const [productList, setProductList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [selectedSubcat, setSelectedSubcat] = useState(null);
  const customer = JSON.parse(localStorage.getItem("customer"));
  const [form, setForm] = useState({
    product_id: '',
    category_id: '',
    sub_category_id: '',
    subject: '',
    description: '',
    customer
  });

  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true);
  const permParams = "support/tickets";

  const fetchPermissions = async () => {
    setFeaturePermissionsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
      const response = await API.get(url, { headers: getApiHeaders() });
      setFeaturePermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setFeaturePermissions([]);
    } finally {
      setFeaturePermissionsLoading(false);
    }
  };
  useEffect(() => {
    fetchPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ticketCreatePermission = hasPermission("ticket-create", featurePermissions);


  const getHeaders = () => ({
    'x-client-key': "aa64d3b756061b158232ac71c7cb76b7c7432daf581fdfa9821bb4a10a461385",
    'ngrok-skip-browser-warning': 'true',
    'x-customer': customer ? JSON.stringify(customer) : undefined,
    "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),
  });
  useEffect(() => {
    if (featurePermissionsLoading || !ticketCreatePermission) return;
    loadProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featurePermissionsLoading, ticketCreatePermission]);

  const loadProducts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/product-list`, { headers: getHeaders() });
      const list = data?.data || data?.rows || data;
      setProductList(Array.isArray(list) ? list : []);
    } catch {
      setProductList([]);
    }
  };
  const loadTicketCategories = async () => {
    setForm((f) => ({ ...f, product_id: 2, category_id: '', sub_category_id: '' }));

    try {
      const { data } = await axios.get(`${API_URL}/support-category-by-product/2`, { headers: getHeaders() });
      const list = data?.data || data?.rows || data;
      setCategoryList(Array.isArray(list) ? list : []);
    } catch {
      setCategoryList([]);
    }
  }

  useEffect(() => {
    if (featurePermissionsLoading || !ticketCreatePermission) return;
    loadTicketCategories()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featurePermissionsLoading, ticketCreatePermission]);


  const handleCategoryChange = async (val) => {
    setForm((f) => ({ ...f, category_id: val, sub_category_id: '' }));
    setSubcategoryList([]);
    setSelectedSubcat(null);
    if (!val) return;
    try {
      const { data } = await axios.get(`${API_URL}/support-sub-category-by-category/${val}`, { headers: getHeaders() });
      const list = data?.data || data?.rows || data;
      setSubcategoryList(Array.isArray(list) ? list : []);
    } catch {
      setSubcategoryList([]);
    }
  };

  const handleSubcategoryChange = (val) => {
    setForm((f) => ({ ...f, sub_category_id: val }));
    if (!val) { setSelectedSubcat(null); return; }
    const sub = subcategoryList.find((s) => String(s.value ?? s.id) === String(val));
    setSelectedSubcat(sub || null);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)]);
  };

  const handleFileInput = (e) => {
    setFiles((f) => [...f, ...Array.from(e.target.files)]);
    e.target.value = '';
  };

  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!form.product_id || !form.category_id || !form.sub_category_id || !form.subject || !form.description) {
      toast({ title: 'Please fill all required fields', status: 'warning', duration: 3000 });
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (val) formData.append(key, val);
      });
      files.forEach((file) => formData.append('attachments[]', file));
      await axios.post(`${API_URL}/ticket-create`, formData, { headers: getHeaders() });
      toast({ title: 'Ticket created successfully', status: 'success', duration: 3000 });
      navigate('/support/tickets');
    } catch (err) {
      toast({ title: 'Failed to create ticket', description: String(err), status: 'error', duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyles = {
    size: 'sm',
    borderRadius: 'lg',
    bg: 'white',
    _focus: { borderColor: 'blue.400', boxShadow: '0 0 0 1px #3b82f6' },
  };

  const labelStyles = {
    fontSize: 'xs',
    fontWeight: 'semibold',
    color: 'gray.700',
    mb: 1,
  };

  if (featurePermissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        </BoxWrapper>
      </>
    );
  }

  return (
    <>
      <Header />
      <BoxWrapper>
        <Box>
          <Box py={4} borderBottomWidth="1px" mb={5}>
            <Heading as="h1" size="md" color="gray.900">Create New Ticket</Heading>
            <Text fontSize="xs" color="gray.500" mt={1}>Fill out the form below to submit a support ticket</Text>
          </Box>

          <Grid templateColumns="1fr 320px" gap={5}>
            {/* Left: Form */}
            <GridItem>
              <VStack gap={4} align="stretch">
                <Box bg="white" borderRadius="xl" borderWidth="1px" p={6}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={4} pb={3} borderBottomWidth="1px">
                    Ticket Details
                  </Text>

                  {/* Classification row */}
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                    {/* <FormControl isRequired>
                      <FormLabel {...labelStyles}>Product</FormLabel>
                      <Select
                        placeholder="Select product"
                        {...fieldStyles}
                        value={form.product_id}
                        onChange={(e) => handleProductChange(e.target.value)}
                      >
                        {productList.map((p) => (
                          <option key={p.value ?? p.id} value={p.value ?? p.id}>{p.label ?? p.name}</option>
                        ))}
                      </Select>
                    </FormControl> */}

                    <FormControl isRequired>
                      <FormLabel {...labelStyles}>Category</FormLabel>
                      <Select
                        placeholder="Select category"
                        {...fieldStyles}
                        value={form.category_id}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        isDisabled={!form.product_id}
                        opacity={!form.product_id ? 0.5 : 1}
                      >
                        {categoryList.map((c) => (
                          <option key={c.value ?? c.id} value={c.value ?? c.id}>{c.label ?? c.name}</option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel {...labelStyles}>Subcategory</FormLabel>
                      <Select
                        placeholder="Select subcategory"
                        {...fieldStyles}
                        value={form.sub_category_id}
                        onChange={(e) => handleSubcategoryChange(e.target.value)}
                        isDisabled={!form.category_id}
                        opacity={!form.category_id ? 0.5 : 1}
                      >
                        {subcategoryList.map((s) => (
                          <option key={s.value ?? s.id} value={s.value ?? s.id}>{s.label ?? s.name}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <FormControl mb={4} isRequired>
                    <FormLabel {...labelStyles}>Issue Title</FormLabel>
                    <Input
                      {...fieldStyles}
                      placeholder="Brief description of your issue"
                      value={form.subject}
                      onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    />
                  </FormControl>

                  <FormControl mb={5} isRequired>
                    <FormLabel {...labelStyles}>Issue Details</FormLabel>
                    <Textarea
                      {...fieldStyles}
                      placeholder="Please provide detailed information about your issue, including steps to reproduce, expected behavior, and any error messages..."
                      rows={6}
                      resize="vertical"
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </FormControl>

                  {/* Attachments */}
                  <FormControl>
                    <FormLabel {...labelStyles}>Attachments <Text as="span" color="gray.400" fontWeight="normal">(optional)</Text></FormLabel>
                    <Box
                      onDrop={handleFileDrop}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={isDragging ? 'blue.400' : 'gray.200'}
                      borderRadius="xl"
                      p={6}
                      textAlign="center"
                      bg={isDragging ? 'blue.50' : 'gray.50'}
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
                      onClick={() => document.getElementById('file-upload-input').click()}
                    >
                      <Icon as={BsCloudUpload} color={isDragging ? 'blue.500' : 'gray.400'} boxSize={6} mb={2} />
                      <Text fontSize="xs" color="gray.600" mb={0.5} fontWeight="medium">
                        Drag & drop files here, or <Text as="span" color="blue.500">click to browse</Text>
                      </Text>
                      <Text fontSize="2xs" color="gray.400">Images, PDFs, Word docs up to 5 files</Text>
                      <input
                        id="file-upload-input"
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileInput}
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.log"
                      />
                    </Box>

                    {files.length > 0 && (
                      <VStack mt={2.5} gap={1.5} align="stretch">
                        {files.map((f, i) => (
                          <HStack key={i} px={3} py={2} borderWidth="1px" borderRadius="lg" bg="white" fontSize="xs" borderColor="gray.200">
                            <Icon as={BsFileText} color="blue.400" />
                            <Text flex={1} isTruncated color="gray.700">{f.name}</Text>
                            <Text color="gray.400" flexShrink={0}>({(f.size / 1024).toFixed(0)} KB)</Text>
                            <Button size="xs" variant="ghost" color="gray.400" _hover={{ color: 'red.500' }} onClick={() => removeFile(i)} minW="auto" px={1}>
                              ×
                            </Button>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </FormControl>
                </Box>

                {/* Action Buttons */}
                <HStack gap={3}>
                  {ticketCreatePermission && (
                    <Button
                      flex={1}
                      size="sm"
                      bg="var(--brand-primary)"
                      color="white"
                      _hover={{ bg: 'var(--brand-primary-active)' }}
                      borderRadius="lg"
                      onClick={handleSubmit}
                      isLoading={submitting}
                      loadingText="Creating ticket..."
                      h={9}
                    >
                      Create Ticket
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="lg"
                    h={9}
                    onClick={() => navigate('/support/tickets')}
                    color="gray.600"
                  >
                    Cancel
                  </Button>
                </HStack>
              </VStack>
            </GridItem>

            {/* Right: SLA + Help */}
            <GridItem>
              <VStack gap={4} align="stretch">
                {selectedSubcat && (
                  <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden">
                    <Flex px={4} py={3} bg="blue.50" borderBottomWidth="1px" borderColor="blue.100" alignItems="center" gap={2}>
                      <Icon as={BsClockHistory} color="blue.500" boxSize={3.5} />
                      <Text fontSize="xs" fontWeight="bold" color="blue.800">SLA Commitment</Text>
                    </Flex>
                    <Box p={4}>
                      <VStack gap={3} align="stretch">
                        <HStack gap={3} p={3} bg="blue.50" borderRadius="lg" borderWidth="1px" borderColor="blue.100">
                          <Flex
                            w={8} h={8} borderRadius="full" bg="blue.100"
                            alignItems="center" justifyContent="center" flexShrink={0}
                          >
                            <Icon as={BsClockHistory} color="blue.500" boxSize={4} />
                          </Flex>
                          <Box>
                            <Text fontSize="2xs" color="blue.600" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">First Response</Text>
                            <Text fontSize="sm" fontWeight="bold" color="blue.700">
                              {selectedSubcat.sla?.first_response_time ? `${selectedSubcat.sla.first_response_time} hours` : '-'}
                            </Text>
                          </Box>
                        </HStack>

                        <HStack gap={3} p={3} bg="green.50" borderRadius="lg" borderWidth="1px" borderColor="green.100">
                          <Flex
                            w={8} h={8} borderRadius="full" bg="green.100"
                            alignItems="center" justifyContent="center" flexShrink={0}
                          >
                            <Icon as={BsCheckCircle} color="green.500" boxSize={4} />
                          </Flex>
                          <Box>
                            <Text fontSize="2xs" color="green.600" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">Resolution</Text>
                            <Text fontSize="sm" fontWeight="bold" color="green.700">
                              {selectedSubcat.sla?.resolution_time ? `${selectedSubcat.sla.resolution_time} hours` : '-'}
                            </Text>
                          </Box>
                        </HStack>

                        <Text fontSize="2xs" color="gray.400" textAlign="center" lineHeight={1.5}>
                          Response times based on ticket priority and category
                        </Text>
                      </VStack>
                    </Box>
                  </Box>
                )}

                <Box bg="white" borderRadius="xl" borderWidth="1px" p={5}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={2}>Tips for faster resolution</Text>
                  <VStack gap={2} align="stretch">
                    {[
                      'Include exact error messages',
                      'Describe steps to reproduce',
                      'Attach relevant screenshots',
                      'Mention affected product version',
                    ].map((tip, i) => (
                      <HStack key={i} gap={2} alignItems="flex-start">
                        <Box w={1} h={1} borderRadius="full" bg="blue.400" mt={1.5} flexShrink={0} />
                        <Text fontSize="xs" color="gray.500">{tip}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </GridItem>
          </Grid>
        </Box>
      </BoxWrapper>
    </>
  );
}
